import React, { useState, useEffect } from "react";
import { Lock, Users, CheckCircle2, Download, LogOut, RefreshCw } from "lucide-react";
import { getAdminRegistrations, verifyAdminCode } from "../lib/api";

const BRANCH_LABEL = { "fc-inkad": "FC Inkad", jeugd: "Inkad Jeugd" };
const STORAGE_KEY = "inkad_admin_code";

function toCsv(rows) {
  const cols = ["created_at", "branch", "full_name", "email", "phone", "birth_date", "gender", "address", "program", "amount", "payment_status", "membership_paid"];
  const head = cols.join(";");
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(";")).join("\n");
  return head + "\n" + body;
}

export default function Admin() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [data, setData] = useState(null);
  const [branch, setBranch] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setCode(saved);
      load(saved, "").finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (c = code, b = branch) => {
    setLoading(true);
    setError("");
    try {
      const res = await getAdminRegistrations(c, b || undefined);
      setData(res);
      setAuthed(true);
    } catch {
      setError("Ongeldige toegangscode of fout bij laden.");
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
    }
    setLoading(false);
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await verifyAdminCode(code);
      localStorage.setItem(STORAGE_KEY, code);
      await load(code, "");
    } catch {
      setError("Ongeldige toegangscode.");
      setLoading(false);
    }
  };

  const download = () => {
    const blob = new Blob([toCsv(data.registrations)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inkad-inschrijvingen-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] text-white/50" data-testid="admin-checking">Laden…</div>;
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-6" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8">
          <div className="mb-6 flex items-center gap-3 text-white">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#CCFF00] text-black"><Lock size={20} /></span>
            <div>
              <h1 className="font-anton text-2xl uppercase leading-none">Beheer</h1>
              <p className="text-xs text-white/50">Inschrijvingen Inkad</p>
            </div>
          </div>
          <label className="mb-2 block text-sm text-white/70">Toegangscode</label>
          <input
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="admin-code-input"
            className="w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-white outline-none focus:border-[#CCFF00]"
            placeholder="••••••••"
            autoFocus
          />
          {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={loading || !code} data-testid="admin-login-btn"
            className="mt-5 w-full rounded-lg bg-[#CCFF00] py-3 font-bold uppercase text-black transition-opacity hover:opacity-90 disabled:opacity-40">
            {loading ? "Bezig…" : "Inloggen"}
          </button>
        </form>
      </div>
    );
  }

  const rows = data?.registrations || [];
  const filtered = branch ? rows.filter((r) => r.branch === branch) : rows;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-white/[0.02]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#CCFF00] text-black"><Users size={18} /></span>
            <h1 className="font-anton text-2xl uppercase">Inschrijvingen</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} data-testid="admin-refresh" className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm hover:border-[#CCFF00]/50">
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Vernieuw
            </button>
            <button onClick={download} disabled={!filtered.length} data-testid="admin-export" className="flex items-center gap-2 rounded-lg bg-[#CCFF00] px-3 py-2 text-sm font-bold text-black disabled:opacity-40">
              <Download size={15} /> Export CSV
            </button>
            <button onClick={() => { setAuthed(false); setData(null); setCode(""); localStorage.removeItem(STORAGE_KEY); }} data-testid="admin-logout" className="flex items-center gap-2 rounded-lg border border-white/15 px-3 py-2 text-sm hover:border-red-400/50">
              <LogOut size={15} /> Uitloggen
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 grid grid-cols-2 gap-4 sm:max-w-md">
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="admin-stat-total">
            <div className="text-3xl font-bold">{filtered.length}</div>
            <div className="text-sm text-white/50">Totaal inschrijvingen</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="admin-stat-paid">
            <div className="flex items-center gap-2 text-3xl font-bold text-[#CCFF00]">
              <CheckCircle2 size={24} />{filtered.filter((r) => r.membership_paid).length}
            </div>
            <div className="text-sm text-white/50">Betaald</div>
          </div>
        </div>

        <div className="mb-4 flex gap-2">
          {["", "fc-inkad", "jeugd"].map((b) => (
            <button key={b || "all"} onClick={() => setBranch(b)} data-testid={`admin-filter-${b || "all"}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${branch === b ? "bg-[#CCFF00] text-black" : "border border-white/15 text-white/70 hover:border-[#CCFF00]/40"}`}>
              {b ? BRANCH_LABEL[b] : "Alles"}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full min-w-[900px] text-sm" data-testid="admin-table">
            <thead>
              <tr className="bg-white/[0.04] text-left text-xs uppercase tracking-wider text-white/50">
                <th className="px-4 py-3">Datum</th>
                <th className="px-4 py-3">Tak</th>
                <th className="px-4 py-3">Naam</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Inschrijving</th>
                <th className="px-4 py-3">Bedrag</th>
                <th className="px-4 py-3">Betaling</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.id || i} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`admin-row-${i}`}>
                  <td className="px-4 py-3 text-white/60 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><span className="rounded bg-white/10 px-2 py-0.5 text-xs">{BRANCH_LABEL[r.branch] || r.branch}</span></td>
                  <td className="px-4 py-3 font-semibold">{r.full_name || `${r.first_name || ""} ${r.last_name || ""}`}</td>
                  <td className="px-4 py-3 text-white/70">
                    <div>{r.email}</div>
                    <div className="text-white/40">{r.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-white/80">{r.program}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.amount ? `€${r.amount}` : "—"}</td>
                  <td className="px-4 py-3">
                    {r.membership_paid ? (
                      <span className="rounded-full bg-[#CCFF00]/15 px-2.5 py-1 text-xs font-semibold text-[#CCFF00]">Betaald</span>
                    ) : r.requires_payment ? (
                      <span className="rounded-full bg-amber-400/15 px-2.5 py-1 text-xs font-semibold text-amber-300">{r.payment_status || "openstaand"}</span>
                    ) : (
                      <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs text-white/50">n.v.t.</span>
                    )}
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-white/40" data-testid="admin-empty">Geen inschrijvingen gevonden.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
